import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import LoadingIcon from 'components/LoadingIcon';
import LegacySelect from 'components/LegacyQuestion/LegacySelect';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';


// StyleGuide = organisms/forms/

const CmeCoach = ({
  cmeCoach,
  specialties,
  usStates,
  productPages,
  onSearch,
  onSubmitEmail,
}) => {
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState(0);
  const [selectedStateId, setSelectedStateId] = useState(0);
  const [inputEmail, setInputEmail] = useState('');
  const elRef = useRef(null);
  const { searchedSpecialtyId, searchedStateId } = cmeCoach;

  useEffect(() => {
    // Scroll back to top when search changes
    window.requestAnimationFrame(() => {
      if (elRef.current) elRef.current.scrollTop = 0;
    });
  }, [searchedSpecialtyId, searchedStateId]);

  const handleSearch = () => {
    if (selectedStateId || selectedSpecialtyId) {
      onSearch(selectedSpecialtyId, selectedStateId);
    }
  };

  const onSubmitEmailForm = (ev) => {
    ev.preventDefault();
    onSubmitEmail(inputEmail)
      .then(() => setInputEmail(''))
      .catch(() => {});
  };

  const renderProduct = (product, index) => (
    <li
      className="CmeProduct"
      key={index}
    >
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={`/${product.path}`}
      >
        <h2>{product.name}</h2>
        <ul className="cme-product-details">
          {product.cme_details.map((item, index2) => (
            <li key={index2}>{item}</li>
          ))}
        </ul>

        {product.cme_reason_to_buy_blurb && (
          <p className="cme-product-reason">{product.cme_reason_to_buy_blurb}</p>
        )}

        <span className="link-blue">Learn more</span>
      </a>
    </li>
  );

  const renderRequirements = (mocYear, requirements, organization, index) => (
    <div key={index} className={`license-req year-${mocYear}`}>
      {mocYear !== '' && (
        <h3 className="underline">
          By the end of year {mocYear}
        </h3>
      )}
      <div className={`req-${mocYear}`}>
        {requirements.map((requirement, index2) => (
          <div key={index2}>
            <i className="fa fa-check" />
            {requirement.description}
          </div>
        ))}
      </div>
    </div>
  );

  const renderLicense = (license, organization, index) => {
    const noYearRequirements = license.requirements_by_moc_year[''];
    const yearRequirements = { ...license.requirements_by_moc_year, '': undefined };
    const hasRequirements = Object.keys(license.requirements_by_moc_year).length > 0;

    return (
      <div
        key={index}
        className="CmeCoachLicense"
      >
        <h2>
          {organization.name}
          <a
            href={organization.website}
            rel="noopener noreferrer"
            target="_blank"
            className="cme-organization-website"
          >
            &#8689;
          </a>
        </h2>

        <div className="license-glance">
          <h3>At a Glance</h3>

          {license.renewal_timeframe && (
            <p>{license.cme_hours} total hours every {license.renewal_timeframe} years</p>
          )}

          {!hasRequirements && (
            <p>No requirements for {organization.name}</p>
          )}

          {noYearRequirements && renderRequirements('', noYearRequirements, organization, -1)}

          { Object.entries(yearRequirements).map((e, index2) => renderRequirements(e[0], e[1] || [], organization, index2)) }
        </div>
        <div className="license-prop-description">
          {license.description}
        </div>
        <a href={license.source} rel="noopener noreferrer" target="_blank">Source</a>
      </div>
    );
  };

  const { organizations, isSearching } = cmeCoach;

  const filteredSpecialties = Object.values(specialties).filter(specialty => specialty.cme_organization_id);
  const specialtyOptions = filteredSpecialties.map(specialty => ({
    value: specialty.id,
    name: specialty.name
  }));

  const stateOptions = Object.values(usStates).map(state => ({
    value: state.id,
    name: state.name
  }));

  const searchedSpecialty = specialties[cmeCoach.searchedSpecialtyId];
  const searchedState = usStates[cmeCoach.searchedStateId];

  const hasSearched = searchedSpecialty || searchedState;

  const titles = [];
  if (searchedSpecialty) titles.push(searchedSpecialty.name);
  if (searchedState) titles.push(searchedState.name);
  const title = `${titles.join(' & ')} Requirements`;

  const disclaimer = 'Each state and medical specialty board has their own '
    + 'set of CME requirements to maintain your license and board certification.';

  return (
    <div className="CmeCoach">
      <h1 className="cme-coach-heading">CME Coach&trade; <span>by BoardVitals</span></h1>
      <h2 className="cme-coach-subheading">Score a free report outlining your specific Specialty and State Requirements</h2>
      <div className="cme-coach-wrapper">
        <div className="cme-coach-sections">
          {hasSearched && (
            <div className="cme-coach-section cme-coach-section-left">
              <div className="CmeCoachProducts">
                <h1>Recommended CME</h1>
                <ul>
                  { productPages.map((product, index) => renderProduct(product, index)) }
                </ul>
              </div>
            </div>
          )}
          <div className="cme-coach-section cme-coach-section-right">
            <div className="CmeCoachSearch d-md-flex">
              <div className="form-group">
                <LegacySelect
                  name="specialty"
                  label="Specialty"
                  placeholder="Select a Specialty"
                  onChange={id => setSelectedSpecialtyId(parseInt(id))}
                  options={specialtyOptions}
                  value={selectedSpecialtyId}
                  disabled={isSearching}
                />
              </div>
              <div className="form-group">
                <LegacySelect
                  name="state"
                  label="State"
                  placeholder="Select a State"
                  onChange={id => setSelectedStateId(id)}
                  options={stateOptions}
                  value={selectedStateId}
                  disabled={isSearching}
                />
              </div>
              <div className="form-group">
                <Button
                  type="submit"
                  disabled={isSearching}
                  className="find-cme-ajax"
                  onClick={handleSearch}
                >
                  Go
                </Button>
              </div>
            </div>

            {isSearching && (
              <LoadingIcon className="cme-coach-loading" />
            )}

            {!isSearching && hasSearched && (
              <div
                className="CmeCoachRequirements"
                ref={elRef}
              >
                <h1>{title}</h1>

                <div className="list">
                  {organizations.map((organization, index) => (
                    organization.licenses.map((license, index2) => (
                      renderLicense(license, organization, index * index2)
                    ))
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        {hasSearched && (
          <div className="disclaimer">{disclaimer}</div>
        )}
      </div>
      {hasSearched && (
        <form onSubmit={onSubmitEmailForm}>
          <h3 className="cme-coach-email-label">Get a copy of your CME requirements for easy reference</h3>
          <div className="cme-coach-email-form-wrapper d-md-flex">
            <div className="form-group">
              <Input
                name="cme-coach-email"
                type="email"
                placeholder="Email"
                required
                value={inputEmail}
                onChange={value => setInputEmail(value)}
                disabled={cmeCoach.isSendingEmail}
              />
            </div>
            <div className="form-group">
              <Button
                type="submit"
                disabled={cmeCoach.isSendingEmail}
              >
                Send
              </Button>
            </div>
            {cmeCoach.isSendingEmail && (
              <div className="form-group">
                <LoadingIcon hideLabel />
              </div>
            )}

            {(!cmeCoach.isSendingEmail && cmeCoach.sendEmailStatus !== null) && (
              <div className="form-group">
                {cmeCoach.sendEmailStatus ? 'Email sent!' : 'There was an error sending your email. Please try again later or with a different email address.'}
              </div>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

CmeCoach.propTypes = {
  cmeCoach: PropTypes.object.isRequired,
  specialties: PropTypes.object.isRequired,
  usStates: PropTypes.object.isRequired,
  productPages: PropTypes.object.isRequired,
  onSearch: PropTypes.func.isRequired,
  onSubmitEmail: PropTypes.func.isRequired,
};

export default CmeCoach;
